import { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Turnstile, type TurnstileInstance } from '@marsidev/react-turnstile';
import { useAuth } from '@/hooks/useAuth';
import Layout from '@/components/layout/Layout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Heart, Mail, Lock, Sparkles, MailCheck } from 'lucide-react';
import { toast } from 'sonner';
import { PasswordStrength } from '@/components/auth/password-strength';
import { registerSchema } from '@/lib/auth-schema';

// Cloudflare's published always-pass dev sitekey. Real production sitekey is
// set via VITE_TURNSTILE_SITE_KEY by infra.
const TURNSTILE_DEV_SITE_KEY = '1x00000000000000000000AA';
const TURNSTILE_SITE_KEY =
  import.meta.env.VITE_TURNSTILE_SITE_KEY ?? TURNSTILE_DEV_SITE_KEY;

const Auth = () => {
  const navigate = useNavigate();
  const { signIn, signUp, signInWithMagicLink, resendConfirmation } = useAuth();
  const [isLoading, setIsLoading] = useState(false);

  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [magicLinkLoading, setMagicLinkLoading] = useState(false);

  const [registerEmail, setRegisterEmail] = useState('');
  const [registerPassword, setRegisterPassword] = useState('');
  const [registerConfirmPassword, setRegisterConfirmPassword] = useState('');
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const turnstileRef = useRef<TurnstileInstance | null>(null);

  // Email-confirmation pending panel state — shared by signup and email-not-confirmed signin.
  const [pendingEmail, setPendingEmail] = useState<string | null>(null);
  const [resendLoading, setResendLoading] = useState(false);

  const resetTurnstile = () => {
    setCaptchaToken(null);
    turnstileRef.current?.reset();
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!captchaToken) {
      toast.error('Completa la verificación para continuar');
      return;
    }
    setIsLoading(true);

    const { error } = await signIn(loginEmail, loginPassword, captchaToken);

    if (error) {
      resetTurnstile();
      // Supabase returns code 'email_not_confirmed' (and message contains it as a fallback).
      const code = (error as { code?: string }).code;
      const looksUnconfirmed =
        code === 'email_not_confirmed' ||
        /email.*not.*confirmed/i.test(error.message);

      if (looksUnconfirmed) {
        setPendingEmail(loginEmail);
      } else {
        toast.error('Error al iniciar sesión: ' + error.message);
      }
    } else {
      toast.success('¡Bienvenido!');
      navigate('/dashboard');
    }

    setIsLoading(false);
  };

  const handleMagicLink = async () => {
    if (!loginEmail) {
      toast.error('Ingresa tu correo electrónico');
      return;
    }
    if (!captchaToken) {
      toast.error('Completa la verificación para continuar');
      return;
    }
    setMagicLinkLoading(true);
    const { error } = await signInWithMagicLink(loginEmail, captchaToken);
    if (error) {
      toast.error('No pudimos enviar el enlace: ' + error.message);
    } else {
      toast.success('Te enviamos un enlace. Revisa tu correo.');
    }
    resetTurnstile();
    setMagicLinkLoading(false);
  };

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();

    const parsed = registerSchema.safeParse({
      email: registerEmail,
      password: registerPassword,
      confirmPassword: registerConfirmPassword,
    });

    if (!parsed.success) {
      const firstError = parsed.error.issues[0]?.message ?? 'Datos inválidos';
      toast.error(firstError);
      return;
    }

    if (!captchaToken) {
      toast.error('Completa la verificación para continuar');
      return;
    }

    setIsLoading(true);

    const { error } = await signUp(registerEmail, registerPassword, { captchaToken });

    if (error) {
      toast.error('Error al registrarse: ' + error.message);
      resetTurnstile();
    } else {
      setPendingEmail(registerEmail);
    }

    setIsLoading(false);
  };

  const handleResend = async () => {
    if (!pendingEmail) return;
    if (!captchaToken) {
      toast.error('Completa la verificación para reenviar');
      return;
    }
    setResendLoading(true);
    const { error } = await resendConfirmation(pendingEmail, captchaToken);
    resetTurnstile();
    if (error) {
      toast.error('No pudimos reenviar el correo: ' + error.message);
    } else {
      toast.success('Correo reenviado. Revisa tu bandeja de entrada.');
    }
    setResendLoading(false);
  };

  if (pendingEmail) {
    return (
      <Layout>
        <div className="min-h-[80vh] flex items-center justify-center py-12 px-4">
          <Card className="w-full max-w-md">
            <CardHeader className="text-center">
              <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-primary/10">
                <MailCheck className="h-6 w-6 text-primary" />
              </div>
              <CardTitle className="text-2xl font-display">Confirma tu correo</CardTitle>
              <CardDescription>
                Te enviamos un correo a <strong>{pendingEmail}</strong>. Confirma para acceder.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <p className="text-xs text-muted-foreground text-center">
                Puede tardar un minuto en llegar. Si no lo ves, revisa spam o reenvíalo.
              </p>
              <div className="flex justify-center">
                <Turnstile
                  ref={turnstileRef}
                  siteKey={TURNSTILE_SITE_KEY}
                  onSuccess={(token) => setCaptchaToken(token)}
                  onExpire={() => setCaptchaToken(null)}
                  onError={() => setCaptchaToken(null)}
                  options={{ theme: 'auto' }}
                />
              </div>
              <Button
                onClick={handleResend}
                className="w-full"
                disabled={resendLoading || !captchaToken}
              >
                {resendLoading ? 'Enviando...' : 'Reenviar correo'}
              </Button>
              <Button
                type="button"
                variant="ghost"
                className="w-full"
                onClick={() => setPendingEmail(null)}
              >
                Volver
              </Button>
            </CardContent>
          </Card>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="min-h-[80vh] flex items-center justify-center py-12 px-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-primary/10">
              <Heart className="h-6 w-6 text-primary" />
            </div>
            <CardTitle className="text-2xl font-display">Bienvenidos</CardTitle>
            <CardDescription>
              Crea y gestiona tus invitaciones de boda digitales
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Tabs defaultValue="login" className="w-full">
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="login">Iniciar Sesión</TabsTrigger>
                <TabsTrigger value="register">Registrarse</TabsTrigger>
              </TabsList>

              <TabsContent value="login" className="space-y-4 mt-4">
                <form onSubmit={handleLogin} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="login-email">Correo electrónico</Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                      <Input
                        id="login-email"
                        type="email"
                        placeholder="tu@email.com"
                        className="pl-10"
                        value={loginEmail}
                        onChange={(e) => setLoginEmail(e.target.value)}
                        required
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="login-password">Contraseña</Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                      <Input
                        id="login-password"
                        type="password"
                        placeholder="••••••••"
                        className="pl-10"
                        value={loginPassword}
                        onChange={(e) => setLoginPassword(e.target.value)}
                        required
                      />
                    </div>
                  </div>
                  <div className="flex justify-center">
                    <Turnstile
                      ref={turnstileRef}
                      siteKey={TURNSTILE_SITE_KEY}
                      onSuccess={(token) => setCaptchaToken(token)}
                      onExpire={() => setCaptchaToken(null)}
                      onError={() => setCaptchaToken(null)}
                      options={{ theme: 'auto' }}
                    />
                  </div>
                  <Button type="submit" className="w-full" disabled={isLoading || !captchaToken}>
                    {isLoading ? 'Cargando...' : 'Iniciar Sesión'}
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    className="w-full"
                    onClick={handleMagicLink}
                    disabled={magicLinkLoading || !captchaToken}
                  >
                    <Sparkles className="mr-2 h-4 w-4" />
                    {magicLinkLoading ? 'Enviando...' : 'Iniciar con link mágico'}
                  </Button>
                  <Button
                    type="button"
                    variant="link"
                    className="w-full text-muted-foreground"
                    onClick={() => navigate('/reset-password')}
                  >
                    ¿Olvidaste tu contraseña?
                  </Button>
                </form>
              </TabsContent>

              <TabsContent value="register" className="space-y-4 mt-4">
                <form onSubmit={handleRegister} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="register-email">Correo electrónico</Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                      <Input
                        id="register-email"
                        type="email"
                        placeholder="tu@email.com"
                        className="pl-10"
                        value={registerEmail}
                        onChange={(e) => setRegisterEmail(e.target.value)}
                        required
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="register-password">Contraseña</Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                      <Input
                        id="register-password"
                        type="password"
                        placeholder="Mínimo 10 caracteres, 1 mayúscula y 1 número"
                        className="pl-10"
                        value={registerPassword}
                        onChange={(e) => setRegisterPassword(e.target.value)}
                        required
                      />
                    </div>
                    <PasswordStrength password={registerPassword} />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="register-confirm">Confirmar contraseña</Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                      <Input
                        id="register-confirm"
                        type="password"
                        placeholder="Repite tu contraseña"
                        className="pl-10"
                        value={registerConfirmPassword}
                        onChange={(e) => setRegisterConfirmPassword(e.target.value)}
                        required
                      />
                    </div>
                  </div>
                  <div className="flex justify-center">
                    <Turnstile
                      ref={turnstileRef}
                      siteKey={TURNSTILE_SITE_KEY}
                      onSuccess={(token) => setCaptchaToken(token)}
                      onExpire={() => setCaptchaToken(null)}
                      onError={() => setCaptchaToken(null)}
                      options={{ theme: 'auto' }}
                    />
                  </div>
                  <Button
                    type="submit"
                    className="w-full"
                    disabled={isLoading || !captchaToken}
                  >
                    {isLoading ? 'Cargando...' : 'Crear Cuenta'}
                  </Button>
                </form>
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

export default Auth;
